import { apiHelpers, extractApiData } from '@/api/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Company, CompanyManagerResponse } from '@packages/types/company';
import type { SiteResponse } from '@packages/types/site/dto';
// Query Keys
export const companyManagementKeys = {
  all: ['companies'] as const,
  allList: () => [...companyManagementKeys.all, 'all'] as const,
  details: () => [...companyManagementKeys.all, 'detail'] as const,
  detail: (id: number) => [...companyManagementKeys.details(), id] as const,
  sites: () => [...companyManagementKeys.all, 'sites'] as const,
  sitesByCompany: (companyId: number) => [...companyManagementKeys.sites(), companyId] as const,
  devices: () => [...companyManagementKeys.all, 'devices'] as const,
};

// 모든 회사 목록 조회 (Admin만 가능)
export const useGetAllCompanies = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: companyManagementKeys.allList(),
    queryFn: async () => {
      const response = await apiHelpers.get<Company[]>('company.all');
      return extractApiData(response);
    },
    ...options,
  });
};

// 사용자의 회사 정보 조회
export const useGetUserCompany = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: companyManagementKeys.all,
    queryFn: async () => {
      const response = await apiHelpers.get<Company>('company.getUserCompany');
      return extractApiData(response);
    },
    ...options,
  });
};

// 회사의 CompanyManager 정보 조회
export const useGetCompanyManager = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...companyManagementKeys.all, 'company-manager'],
    queryFn: async () => {
      const response = await apiHelpers.get<CompanyManagerResponse | null>(
        'company.getCompanyManager'
      );
      return extractApiData(response);
    },
    ...options,
  });
};

// 사용자가 접근 가능한 모든 사이트 조회
export const useGetMySites = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['mySites'],
    queryFn: async () => {
      const response = await apiHelpers.get<SiteResponse[]>('site.getMySites');
      return extractApiData(response);
    },
    ...options,
  });
};

// 회사별 사이트 목록 조회
export const useGetSitesByCompanyId = (companyId: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: companyManagementKeys.sitesByCompany(companyId),
    queryFn: async () => {
      const response = await apiHelpers.get<SiteResponse[]>('site.getByCompanyId', {
        companyId,
      });
      return extractApiData(response);
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!companyId,
  });
};

// 회사 삭제 (Admin만 가능)
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: number) => {
      const response = await apiHelpers.delete<null>('company.delete', { id: companyId });
      return extractApiData(response);
    },
    onSuccess: () => {
      // 회사 목록 새로고침
      queryClient.invalidateQueries({ queryKey: companyManagementKeys.allList() });
      queryClient.invalidateQueries({ queryKey: companyManagementKeys.all });
    },
  });
};
